import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { normalizeVideoUrl, isEmbeddable } from '../../services/videoUtils';

interface LightboxProps {
  src: string;
  type: 'image' | 'video';
  onClose: () => void;
}

export const Lightbox: React.FC<LightboxProps> = ({ src, type, onClose }) => {
  const directUrl = type === 'video' ? normalizeVideoUrl(src) : src;
  const isDriveEmbed = type === 'video' && directUrl ? isEmbeddable(directUrl) : false;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (type !== 'video' || isDriveEmbed || !videoRef.current) return;
    const v = videoRef.current;
    try {
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
    } catch {
      /* ignore */
    }
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, [type, isDriveEmbed, directUrl]);

  return (
    <div className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex items-center justify-center animate-fade-in touch-manipulation">
      <div
        className="absolute inset-0 w-full h-full p-2 sm:p-4 md:p-10 flex items-center justify-center cursor-pointer"
        onClick={onClose}
      >
        <div
          className={`relative shadow-2xl rounded-xl sm:rounded-2xl overflow-hidden bg-black cursor-default border border-white/10 flex items-center justify-center max-h-[100dvh] w-full ${
            isDriveEmbed ? 'aspect-video max-w-5xl h-[min(80dvh,56vw)]' : ''
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {type === 'video' ? (
            isDriveEmbed ? (
              <iframe
                src={directUrl}
                className="w-full h-full min-h-[200px] border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                title="Video preview"
              />
            ) : (
              <video
                ref={videoRef}
                src={directUrl}
                controls
                autoPlay
                playsInline
                preload="auto"
                className="max-h-[88dvh] w-full max-w-full object-contain bg-black"
              />
            )
          ) : (
            <img
              src={src}
              alt="Full view"
              className="max-h-[88dvh] w-full max-w-full object-contain"
              decoding="async"
            />
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={onClose}
        className="absolute top-[max(1rem,env(safe-area-inset-top))] right-[max(1rem,env(safe-area-inset-right))] z-[210] p-2.5 sm:p-3 bg-black/60 hover:bg-white/20 border border-white/10 rounded-full text-white transition-all hover:scale-110 active:scale-95 backdrop-blur-md group"
        aria-label="Close"
      >
        <X size={26} className="sm:scale-110 group-hover:text-red-500 transition-colors" />
      </button>
    </div>
  );
};
